import {
  DailyExpenseDay,
  DailyExpenseEventAction,
  DailyExpenseStatus,
} from "../../types/daily-expense.type";

/**
 * What a day's status chip says. ยังไม่กรอก is a day with no document, and ล็อก is any day of a
 * closed month — neither is a stored status, which is why the chip has its own set.
 */
export type DailyExpenseChipKind = "NOT_STARTED" | DailyExpenseStatus | "LOCKED";

export const CHIP_LABEL: Record<DailyExpenseChipKind, string> = {
  NOT_STARTED: "ยังไม่กรอก",
  DRAFT: "ร่าง",
  SUBMITTED: "รออนุมัติ",
  APPROVED: "อนุมัติ",
  REJECTED: "ตีกลับ",
  LOCKED: "ล็อก",
};

/** A closed month locks every day of it, whatever the document had reached. */
export const chipKindOf = (
  status: DailyExpenseStatus | null,
  isLocked: boolean,
): DailyExpenseChipKind => {
  if (isLocked) return "LOCKED";
  return status ?? "NOT_STARTED";
};

/** The branch still holds the day: nothing written yet, a draft, or one head office sent back. */
export const isBranchEditableStatus = (status: DailyExpenseStatus | null) =>
  status === null || status === "DRAFT" || status === "REJECTED";

/** The S2 list's row link: กรอก a blank day, แก้ไข one the branch still holds, ดู the rest. */
export const listActionLabel = (
  status: DailyExpenseStatus | null,
  isLocked: boolean,
): string => {
  if (isLocked || !isBranchEditableStatus(status)) return "ดู";
  return status === null ? "กรอก" : "แก้ไข";
};

export const EVENT_LABEL: Record<DailyExpenseEventAction, string> = {
  SUBMIT: "ส่งให้ HO",
  SUBMIT_NO_EXPENSE: "ส่งให้ HO (ไม่มีค่าใช้จ่าย)",
  APPROVE: "อนุมัติ",
  REJECT: "ตีกลับ",
};

/** The line above a day the branch cannot change, saying why; null while the branch may edit. */
export interface ReadOnlyNotice {
  severity: "info" | "warning" | "success";
  text: string;
}

export const readOnlyNoticeOf = (
  day: DailyExpenseDay,
  todayDate: string,
  formatTime: (timestamp: string | null) => string,
): ReadOnlyNotice | null => {
  if (day.actions.can_edit) return null;
  if (day.is_locked) return { severity: "info", text: "ปิดเดือนแล้ว เอกสารถูกล็อก แก้ไขไม่ได้" };
  const status = day.document?.status ?? null;
  if (status === "SUBMITTED") {
    return {
      severity: "warning",
      text: `ส่งให้ HO แล้วเมื่อ ${formatTime(day.document?.submitted_at ?? null)} · รอ HO อนุมัติ แก้ไขไม่ได้`,
    };
  }
  if (status === "APPROVED") {
    return {
      severity: "success",
      text: "HO อนุมัติแล้ว · ถ้าต้องแก้ ติดต่อ HO ให้ตีกลับก่อนปิดเดือน",
    };
  }
  if (day.business_date > todayDate) {
    return { severity: "info", text: "ยังไม่ถึงวันนี้ กรอกค่าใช้จ่ายล่วงหน้าไม่ได้" };
  }
  // The server refuses this account on the day for another reason (e.g. a branch role on a
  // head-office token); say so rather than leave a form with no buttons unexplained.
  return { severity: "info", text: "บัญชีนี้บันทึกค่าใช้จ่ายของวันนี้ไม่ได้" };
};

/**
 * Head office sent the day back: the red banner names who and why. Once the branch saves it again
 * it is a draft, and the banner goes (the server keeps the old reason until the next submit).
 */
export const isSentBack = (day: DailyExpenseDay): boolean => day.document?.status === "REJECTED";
